import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/components/ui/dropdown-menu'
import {DataTable, type Column} from '@/components/data-table'
import {ListCard, ListEmptyState} from '@/components/section-card'
import {PageHeading} from '@/components/page-heading'
import {NoAccessState} from '@/components/no-access-state'
import {FieldError} from '@/components/field-error'
import {
  PersonaLicenciaDialog,
  RosterActivationConfirms,
  RosterCreateDialog,
  identityPayload,
  type PersonaIdentity,
  type RosterCreateResult,
} from '@/components/roster'
import {useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {useTableState} from '@/hooks/common/use-table-state'
import {
  ADMINS_PAGE_SIZE,
  mintAdminLicenciaUploadTarget,
  useAdminLicenciaMutations,
  useAdmins,
  useCreateAdmin,
  useDeactivateAdmin,
  useReactivateAdmin,
  useUpdateAdmin,
  type Admin,
} from '@/hooks/inci/consola/use-admins'
import {translateApiError} from '@/lib/errors'
import {ConfirmarCuentaDeInciDialog} from '@/components/confirmar-identidad-dialog'
import {pideConfirmarIdentidad} from '@/hooks/platform/use-access'
import {validateEmail, validatePhone, validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'
import {SearchInput} from '@/components/ui/search-input'

function licenciaTone(estado: Admin['licencia']['estado']): 'green' | 'red' | 'amber' | 'neutral' {
  if (estado === 'verificada') return 'green'
  if (estado === 'rechazada') return 'red'
  if (estado === 'por_verificar') return 'amber'
  return 'neutral'
}

function LicenciaBadge({licencia}: {licencia: Admin['licencia']}) {
  const {t} = useTranslation()
  const key = licencia.estado ?? 'none'
  return <Tag tone={licenciaTone(licencia.estado)}>{t(`admins.licenciaEstado.${key}`)}</Tag>
}

const EMPTY_IDENTITY: PersonaIdentity = {
  name: '',
  last_name: '',
  cedula: '',
  tipo_documento: 'CC',
  email: '',
  phone: '',
}

function CreateAdminDialog({open, onClose}: {open: boolean; onClose: () => void}) {
  const create = useCreateAdmin()
  const [identity, setIdentity] = useState<PersonaIdentity>(EMPTY_IDENTITY)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<RosterCreateResult | null>(null)
  const [confirmando, setConfirmando] = useState(false)

  function reset() {
    setIdentity(EMPTY_IDENTITY)
    setError(null)
    setResult(null)
    setConfirmando(false)
    create.reset()
  }

  function crear(confirmar_identidad: boolean) {
    setError(null)
    create.mutate(
      {...identityPayload(identity), confirmar_identidad},
      {
        onSuccess: (data) => {
          setConfirmando(false)
          setResult({inviteToken: data.invite_token ?? null})
        },
        onError: (err) => {
          setConfirmando(false)
          if (pideConfirmarIdentidad(err)) {
            setConfirmando(true)
            return
          }
          setError(translateApiError(err))
        },
      }
    )
  }

  return (
    <>
      <RosterCreateDialog
        open={open}
        onClose={onClose}
        tKey="admins.create"
        testPrefix="admin"
        result={result}
        error={error}
        pending={create.isPending}
        identity={identity}
        onIdentityChange={setIdentity}
        onSubmit={() => crear(false)}
        onReset={reset}
      />
      <ConfirmarCuentaDeInciDialog
        cedula={identity.cedula.trim()}
        open={confirmando}
        onOpenChange={setConfirmando}
        onConfirm={() => crear(true)}
        pending={create.isPending}
      />
    </>
  )
}

type EditAdminErrors = {name?: string; last_name?: string; email?: string; phone?: string}

function EditAdminDialog({admin, onClose}: {admin: Admin | null; onClose: () => void}) {
  const {t} = useTranslation()
  return (
    <Dialog open={!!admin} onOpenChange={(next) => !next && onClose()}>
      {admin && (
        <DialogContent data-testid="admin-edit-dialog">
          <DialogHeader>
            <DialogTitle>{t('admins.edit.title')}</DialogTitle>
          </DialogHeader>
          {/* `key` por administrador: con `open` controlado desde afuera Radix no avisa la apertura,
              así que el formulario debe nacer con los datos de esa persona (KAI-223). */}
          <EditAdminForm key={admin.user_id} admin={admin} onClose={onClose} />
        </DialogContent>
      )}
    </Dialog>
  )
}

function EditAdminForm({admin, onClose}: {admin: Admin; onClose: () => void}) {
  const {t} = useTranslation()
  const update = useUpdateAdmin()
  const [form, setForm] = useState({
    name: admin.name,
    last_name: admin.last_name,
    email: admin.email ?? '',
    phone: '',
  })
  const [fieldErrors, setFieldErrors] = useState<EditAdminErrors>({})
  const [error, setError] = useState<string | null>(null)

  function set(field: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((f) => ({...f, [field]: e.target.value}))
      setFieldErrors((fe) => ({...fe, [field]: undefined}))
    }
  }

  function blur(field: keyof EditAdminErrors, validate: (raw: string) => string | null) {
    setFieldErrors((fe) => ({...fe, [field]: validate(form[field]) ?? undefined}))
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const errors: EditAdminErrors = {
      name: validateRequired(form.name) ?? undefined,
      last_name: validateRequired(form.last_name) ?? undefined,
      email: form.email ? (validateEmail(form.email) ?? undefined) : undefined,
      phone: form.phone ? (validatePhone(form.phone) ?? undefined) : undefined,
    }
    setFieldErrors(errors)
    if (Object.values(errors).some(Boolean)) return
    update.mutate(
      {
        userId: admin.user_id,
        name: form.name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
      },
      {onSuccess: onClose, onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="admin-edit-name">{t('admins.create.name')}</Label>
        <Input
          maxLength={tope('NOM')}
          id="admin-edit-name"
          data-testid="admin-edit-name"
          value={form.name}
          onChange={set('name')}
          onBlur={() => blur('name', validateRequired)}
          aria-invalid={!!fieldErrors.name}
          required
        />
        <FieldError error={fieldErrors.name} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="admin-edit-last-name">{t('admins.create.lastName')}</Label>
        <Input
          maxLength={tope('NOM')}
          id="admin-edit-last-name"
          value={form.last_name}
          onChange={set('last_name')}
          onBlur={() => blur('last_name', validateRequired)}
          aria-invalid={!!fieldErrors.last_name}
          required
        />
        <FieldError error={fieldErrors.last_name} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="admin-edit-email">{t('admins.create.email')}</Label>
        <Input
          maxLength={tope('MAIL')}
          id="admin-edit-email"
          data-testid="admin-edit-email"
          type="email"
          value={form.email}
          onChange={set('email')}
          onBlur={() => blur('email', (raw) => (raw ? validateEmail(raw) : null))}
          aria-invalid={!!fieldErrors.email}
        />
        <FieldError error={fieldErrors.email} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="admin-edit-phone">{t('admins.create.phone')}</Label>
        <Input
          maxLength={tope('TEL')}
          id="admin-edit-phone"
          className="font-mono"
          value={form.phone}
          onChange={set('phone')}
          onBlur={() => blur('phone', (raw) => (raw ? validatePhone(raw) : null))}
          aria-invalid={!!fieldErrors.phone}
        />
        <FieldError error={fieldErrors.phone} />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" data-testid="admin-edit-submit" disabled={update.isPending}>
          {t('admins.edit.submit')}
        </Button>
      </div>
    </form>
  )
}

function LicenciaDialog({admin, onClose}: {admin: Admin | null; onClose: () => void}) {
  const {save, verify} = useAdminLicenciaMutations()
  return (
    <PersonaLicenciaDialog
      target={
        admin ? {userId: admin.user_id, name: `${admin.name} ${admin.last_name}`, licencia: admin.licencia} : null
      }
      tKey="admins.licenciaDialog"
      testPrefix="admin"
      onClose={onClose}
      mintUploadTarget={mintAdminLicenciaUploadTarget}
      onSave={(input) => save.mutateAsync(input)}
      onVerify={(userId) => verify.mutateAsync(userId).then(onClose)}
      saving={save.isPending}
      verifying={verify.isPending}
    />
  )
}

function AdminActions({
  admin,
  onEdit,
  onLicencia,
  onDeactivate,
  onReactivate,
  testIdPrefix = 'admin',
}: {
  admin: Admin
  onEdit: (a: Admin) => void
  onLicencia: (a: Admin) => void
  onDeactivate: (a: Admin) => void
  onReactivate: (a: Admin) => void
  // Tarjeta móvil y tabla montan a la vez (el corte es CSS), así que los testids necesitan prefijo.
  testIdPrefix?: string
}) {
  const {t} = useTranslation()
  return (
    <div className="flex flex-wrap items-center justify-end gap-1">
      <Button
        size="sm"
        variant="ghost"
        data-testid={`${testIdPrefix}-edit-${admin.user_id}`}
        onClick={() => onEdit(admin)}>
        {t('admins.actions.edit')}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon-xs"
            variant="ghost"
            data-testid={`${testIdPrefix}-more-${admin.user_id}`}
            aria-label={t('admins.actions.more')}>
            <span aria-hidden="true" className="text-base leading-none">
              ⋯
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            data-testid={`${testIdPrefix}-licencia-${admin.user_id}`}
            onSelect={() => onLicencia(admin)}>
            {t('admins.actions.licencia')}
          </DropdownMenuItem>
          {admin.active ? (
            <DropdownMenuItem
              data-testid={`${testIdPrefix}-deactivate-${admin.user_id}`}
              onSelect={() => onDeactivate(admin)}
              className="text-destructive focus:text-destructive">
              {t('admins.actions.deactivate')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              data-testid={`${testIdPrefix}-reactivate-${admin.user_id}`}
              onSelect={() => onReactivate(admin)}>
              {t('admins.actions.reactivate')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function AdminCard({
  admin,
  ...handlers
}: {
  admin: Admin
  onEdit: (a: Admin) => void
  onLicencia: (a: Admin) => void
  onDeactivate: (a: Admin) => void
  onReactivate: (a: Admin) => void
}) {
  const {t} = useTranslation()
  return (
    <li className="surface-divider border-b py-3 last:border-0" data-testid={`admin-card-${admin.user_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">
            {admin.name} {admin.last_name}
          </p>
          <p className="truncate font-mono text-xs text-muted-foreground">{admin.cedula}</p>
          {admin.email && <p className="truncate text-xs text-muted-foreground">{admin.email}</p>}
        </div>
        <Tag tone="neutral">{t(`admins.estado.${admin.active ? 'active' : 'inactive'}`)}</Tag>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <LicenciaBadge licencia={admin.licencia} />
        {!admin.tiene_acceso && admin.active && <Tag tone="amber">{t('admins.acceso.pending')}</Tag>}
      </div>
      <div className="mt-2">
        <AdminActions admin={admin} {...handlers} testIdPrefix="admin-card" />
      </div>
    </li>
  )
}

export function AdminsPage() {
  const {t} = useTranslation()
  const isSystemUser = useIsSystemUser()
  const table = useTableState('name')
  const {data, isLoading} = useAdmins({page: table.page, search: table.search}, isSystemUser)
  const deactivate = useDeactivateAdmin()
  const reactivate = useReactivateAdmin()
  const [creating, setCreating] = useState(false)
  const [editTarget, setEditTarget] = useState<Admin | null>(null)
  const [licenciaTarget, setLicenciaTarget] = useState<Admin | null>(null)
  const [deactivateTarget, setDeactivateTarget] = useState<Admin | null>(null)
  const [reactivateTarget, setReactivateTarget] = useState<Admin | null>(null)

  if (!isSystemUser) return <NoAccessState />

  const handlers = {
    onEdit: setEditTarget,
    onLicencia: setLicenciaTarget,
    onDeactivate: setDeactivateTarget,
    onReactivate: setReactivateTarget,
  }

  const columns: Column<Admin>[] = [
    {
      key: 'name',
      header: t('admins.table.name'),
      cell: (a) => (
        <div className="min-w-0">
          <span className="block truncate font-medium text-foreground">
            {a.name} {a.last_name}
          </span>
          <span className="block truncate font-mono text-xs text-muted-foreground">{a.cedula}</span>
        </div>
      ),
    },
    {
      key: 'email',
      header: t('admins.table.email'),
      width: 'w-48',
      cell: (a) => <span className="block truncate text-xs text-muted-foreground">{a.email}</span>,
    },
    {
      key: 'licencia',
      header: t('admins.table.licencia'),
      width: 'w-32',
      cell: (a) => <LicenciaBadge licencia={a.licencia} />,
    },
    {
      key: 'estado',
      header: t('admins.table.estado'),
      width: 'w-40',
      cell: (a) => (
        <div className="flex flex-wrap items-center gap-1">
          <Tag tone="neutral">{t(`admins.estado.${a.active ? 'active' : 'inactive'}`)}</Tag>
          {!a.tiene_acceso && a.active && <Tag tone="amber">{t('admins.acceso.pending')}</Tag>}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 'w-64',
      className: 'text-right',
      cell: (a) => (
        <div onClick={(e) => e.stopPropagation()}>
          <AdminActions admin={a} {...handlers} />
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-5" data-testid="admins-page">
      <PageHeading
        title={t('admins.title')}
        accion={
          <Button size="sm" data-testid="admin-new" onClick={() => setCreating(true)}>
            {t('admins.new')}
          </Button>
        }
      />

      {isLoading && !data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <ListCard
          toolbar={
            <SearchInput
              value={table.searchInput}
              onChange={table.setSearchInput}
              placeholder={t('admins.searchPlaceholder')}
            />
          }
          footer={
            !!data?.total &&
            data.items.length > 0 && (
              <PaginationBar
                page={table.page}
                pageSize={ADMINS_PAGE_SIZE}
                total={data.total}
                onPageChange={table.setPage}
              />
            )
          }>
          {data && data.items.length === 0 ? (
            <ListEmptyState page title={table.search ? t('admins.noResults') : t('admins.empty')} />
          ) : (
            <>
              <ul className="px-3 lg:hidden">
                {data?.items.map((a) => (
                  <AdminCard key={a.user_id} admin={a} {...handlers} />
                ))}
              </ul>
              <div className="hidden lg:block">
                <DataTable columns={columns} rows={data?.items ?? []} rowKey={(a) => a.user_id} />
              </div>
            </>
          )}
        </ListCard>
      )}

      <CreateAdminDialog open={creating} onClose={() => setCreating(false)} />
      <EditAdminDialog admin={editTarget} onClose={() => setEditTarget(null)} />
      <LicenciaDialog admin={licenciaTarget} onClose={() => setLicenciaTarget(null)} />
      <RosterActivationConfirms
        tKey="admins"
        deactivateTarget={deactivateTarget}
        reactivateTarget={reactivateTarget}
        onCloseDeactivate={() => setDeactivateTarget(null)}
        onCloseReactivate={() => setReactivateTarget(null)}
        deactivate={deactivate}
        reactivate={reactivate}
      />
    </div>
  )
}
